import { blobToBytes } from './blob-bytes';
import { sniffFormat } from './magic-bytes';
import type { DetectionReport, DetectedFormat, FileCategory, FileMetadata, FileType } from './types';

export const EXT_TO_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  avif: 'image/avif',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
  html: 'text/html',
  htm: 'text/html',
  json: 'application/json',
  csv: 'text/csv',
  xml: 'application/xml',
  yaml: 'application/yaml',
  yml: 'application/yaml',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
  glb: 'model/gltf-binary',
  gltf: 'model/gltf+json',
  obj: 'model/obj',
  stl: 'model/stl',
  ply: 'application/ply',
  '3mf': 'model/3mf',
  zip: 'application/zip',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  '7z': 'application/x-7z-compressed',
  rar: 'application/vnd.rar',
};

/** 真实内容必然是二进制容器的扩展名：一旦字节层面不匹配即为畸形文件。 */
const BINARY_ONLY_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'webp',
  'avif',
  'gif',
  'pdf',
  'zip',
  'gz',
  '7z',
  'rar',
  'glb',
  // 旧版 Office 是 OLE2 复合文档；新版 OOXML 是 ZIP 容器。
  'doc',
  'xls',
  'ppt',
]);

/** 基于 ZIP 容器的文档格式：字节层是 ZIP，但不算扩展名与内容冲突。 */
const ZIP_CONTAINER_EXTENSIONS = new Set(['docx', 'xlsx', 'pptx', 'odt', 'ods', 'odp', 'epub', '3mf', 'apk']);

/**
 * 浏览器无法解析的旧版二进制格式：直接告诉用户该怎么处理，
 * 而不是等到点「开始转换」才报一句「不支持」。
 * `.xls` 不在此列 —— SheetJS 原生支持 BIFF，属于可转换范围。
 */
const LEGACY_BINARY_HINTS: Record<string, string> = {
  doc: '旧版二进制 .doc 无法在浏览器内解析：请在 Word 中「另存为」.docx 后重试。',
  ppt: '旧版二进制 .ppt 暂不支持：请另存为 .pptx 后重试。',
  ole2: '这是旧版 Office 二进制容器（.doc/.xls/.ppt 之一）：请另存为新格式（.docx/.xlsx/.pptx）后重试。',
};

const MIME_ALIASES: Record<string, string> = {
  'image/jpg': 'image/jpeg',
  'image/x-png': 'image/png',
  'text/x-markdown': 'text/markdown',
  'text/xml': 'application/xml',
  'application/x-yaml': 'application/yaml',
  'text/yaml': 'application/yaml',
  'application/x-zip-compressed': 'application/zip',
  'model/x.stl-binary': 'model/stl',
  'model/x.stl-ascii': 'model/stl',
  'text/x-csv': 'text/csv',
  'application/vnd.ms-excel': 'text/csv',
};

export function normalizeMime(mime: string | null | undefined): string | null {
  if (!mime) return null;
  const bare = mime.split(';')[0].trim().toLowerCase();
  if (!bare) return null;
  return MIME_ALIASES[bare] ?? bare;
}

export function extensionOf(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? fileName;
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return 'unknown';
  return base.slice(dot + 1).toLowerCase();
}

export function categoryOf(extension: string): FileCategory {
  const ext = extension.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'webp', 'avif', 'gif', 'svg', 'bmp', 'tiff'].includes(ext)) return 'image';
  if (
    [
      'txt',
      'md',
      'markdown',
      'html',
      'htm',
      'json',
      'csv',
      'tsv',
      'xml',
      'yaml',
      'yml',
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
      'odt',
      'rtf',
    ].includes(ext)
  ) {
    return 'document';
  }
  if (['glb', 'gltf', 'obj', 'stl', 'ply', '3mf', 'fbx', 'step', 'iges', 'usd', 'usdz'].includes(ext)) return 'model';
  if (['zip', 'tar', 'gz', 'gzip', '7z', 'rar'].includes(ext)) return 'archive';
  return 'other';
}

export function mimeForExtension(extension: string): string | null {
  return EXT_TO_MIME[extension.toLowerCase()] ?? null;
}

export interface AnalyzeInput {
  name: string;
  size: number;
  declaredMime?: string | null;
  /** 文件头部字节（建议 ≥ 8KB，已足够覆盖全部判据）。 */
  head: Uint8Array;
  /**
   * 可选的文件尾部字节（与 head 同长度量级）。
   *
   * 只有 ZIP 子类型需要它：OOXML 的条目顺序不受规范约束，首个条目若是大体积媒体，
   * 关键条目名会落到头部窗口之外，而中央目录（文件末尾）里始终有一份明文条目名。
   */
  tail?: Uint8Array;
}

/**
 * MIME 等价组。
 *
 * 同一个 MIME 在不同浏览器/不同工具里对应不同扩展名：`application/vnd.ms-excel`
 * 既被 .xls 使用，也被大量 .csv 上报，因此不能只做一对一比较。
 */
const MIME_EQUIVALENTS: Record<string, string[]> = {
  'text/csv': ['application/vnd.ms-excel'],
  'application/vnd.ms-excel': ['text/csv'],
};

/** 旧版 Office 二进制容器（OLE2）同族扩展名：容器判不明时用扩展名兜底。 */
const OLE2_FAMILY_EXTENSIONS = new Set(['doc', 'xls', 'ppt']);

/**
 * 扩展名与 MIME 是否指向同一种内容。
 * `rawMime` 是浏览器上报的原始值（未做别名归一），用于等价组比较。
 */
function mimeMatchesExtension(declaredMime: string | null, rawMime: string | null, extension: string): boolean {
  const expected = mimeForExtension(extension);
  if (!expected) return true;
  const candidates = [declaredMime, rawMime].filter((value): value is string => Boolean(value));
  if (candidates.length === 0) return true;
  return candidates.some(
    (candidate) => candidate === expected || (MIME_EQUIVALENTS[candidate] ?? []).includes(expected),
  );
}

/**
 * 三信号仲裁：
 * 1. 字节嗅探结论优先（唯一定义「内容实际是什么」的信号）；
 * 2. 扩展名与 MIME 作为佐证，冲突时以字节为准并给出告警；
 * 3. 二进制扩展名遇到完全不匹配的内容时判定为畸形文件并拒绝。
 */
export function analyzeFile(input: AnalyzeInput): DetectionReport {
  const declaredExtension = extensionOf(input.name);
  const declaredMime = normalizeMime(input.declaredMime);
  const rawMime = input.declaredMime ? input.declaredMime.split(';')[0].trim().toLowerCase() : null;
  const sniffed: DetectedFormat | null = sniffFormat(input.head, input.tail);
  const warnings: string[] = [];

  const extensionMime = mimeForExtension(declaredExtension);
  const extensionCategory = categoryOf(declaredExtension);

  const extensionMatchesContent = sniffed === null || sniffed.extension === declaredExtension || sameGroup(sniffed.extension, declaredExtension);
  const mimeMatchesContent = sniffed === null || mimeMatchesExtension(declaredMime, rawMime, sniffed.extension);

  let resolved: FileType;
  if (!sniffed) {
    resolved = { extension: declaredExtension, mime: declaredMime ?? extensionMime, category: extensionCategory };
    if (BINARY_ONLY_EXTENSIONS.has(declaredExtension)) {
      return {
        declaredExtension,
        declaredMime,
        sniffed: null,
        resolved,
        extensionMatchesContent: false,
        mimeMatchesContent: false,
        rejected: true,
        rejectReason: `文件内容不是有效的 ${declaredExtension.toUpperCase()} 文件，已拒绝处理。`,
        warnings,
      };
    }
    warnings.push('无法从文件头判定格式，已按扩展名处理，转换前请自行确认。');
    const unresolvedHint = LEGACY_BINARY_HINTS[declaredExtension];
    if (unresolvedHint) warnings.push(unresolvedHint);
  } else {
    const zipContainer = sniffed.extension === 'zip' && ZIP_CONTAINER_EXTENSIONS.has(declaredExtension);
    // OLE2 只判定出「旧版 Office 容器」时，用扩展名兜底（doc/xls/ppt 同族容器）。
    const ole2Fallback = sniffed.extension === 'ole2' && OLE2_FAMILY_EXTENSIONS.has(declaredExtension);
    const declaredByContainer = zipContainer || ole2Fallback;
    resolved = declaredByContainer
      ? { extension: declaredExtension, mime: extensionMime, category: extensionCategory }
      : { extension: sniffed.extension, mime: sniffed.mime, category: sniffed.category };

    if (!extensionMatchesContent && !declaredByContainer) {
      warnings.push(`扩展名 .${declaredExtension} 与文件实际格式（${sniffed.extension}）不符，已按实际格式处理。`);
    }
    // 只在「字节层给出了确定结论」时才对 MIME 提出异议：如果容器子类型没认出来
    // （ZIP 只判到通用 zip、OLE2 只判到同族容器），实际格式就是按扩展名兜底解析的，
    // 此时拿上报 MIME 去跟「application/zip」比较必然不符，属于无意义的告警。
    if (!mimeMatchesContent && !declaredByContainer) {
      warnings.push(`浏览器上报的 MIME（${declaredMime}）与文件实际格式不符，已忽略该 MIME。`);
    }
    if (sniffed.confidence === 'low') {
      warnings.push('格式判定置信度较低，基于文本内容特征推断，必要时请手动确认。');
    }
    if (zipContainer) {
      warnings.push(`只识别到通用 ZIP 容器，已按扩展名 .${declaredExtension.toUpperCase()} 解析；若内容并非该格式，转换会失败。`);
    }
    if (ole2Fallback) {
      warnings.push('只能确认这是旧版 Office 二进制容器，未能识别具体类型；已按扩展名解析。');
    }
    const legacyHint = LEGACY_BINARY_HINTS[resolved.extension];
    if (legacyHint) warnings.push(legacyHint);

    if (BINARY_ONLY_EXTENSIONS.has(declaredExtension) && sniffed.category !== extensionCategory && !declaredByContainer) {
      return {
        declaredExtension,
        declaredMime,
        sniffed,
        resolved,
        extensionMatchesContent: false,
        mimeMatchesContent: false,
        rejected: true,
        rejectReason: `文件扩展名为 .${declaredExtension}，但内容实际是 ${sniffed.extension}（${sniffed.category}），属于明显不符，已拒绝处理。`,
        warnings,
      };
    }
  }

  return {
    declaredExtension,
    declaredMime,
    sniffed,
    resolved,
    extensionMatchesContent,
    mimeMatchesContent,
    rejected: false,
    warnings,
  };
}

/** jpg/jpeg 等别名视为同一组。 */
function sameGroup(a: string, b: string): boolean {
  const groups = [['jpg', 'jpeg'], ['md', 'markdown'], ['yaml', 'yml'], ['html', 'htm'], ['gz', 'gzip']];
  return groups.some((group) => group.includes(a) && group.includes(b));
}

export const MAX_SNIFF_BYTES = 64 * 1024;

export function emptyMetadata(name: string, size: number): FileMetadata {
  return { name, size, type: { extension: extensionOf(name), mime: null, category: 'other' } };
}

/** 面向浏览器的入口：读取文件头（大文件再补读尾部）后完成三信号仲裁。 */
export async function detectFile(file: File): Promise<FileMetadata> {
  const head = await blobToBytes(file.slice(0, MAX_SNIFF_BYTES));
  // 文件超过嗅探窗口时补读尾部：ZIP 的中央目录在末尾，条目名在那里同样有明文副本。
  // 只为 ZIP 子类型判定服务，代价是一次本地读取（不上传、不解压）。
  const tail = file.size > MAX_SNIFF_BYTES ? await blobToBytes(file.slice(-MAX_SNIFF_BYTES)) : undefined;
  const detection = analyzeFile({
    name: file.name,
    size: file.size,
    declaredMime: file.type,
    head,
    tail,
  });
  return {
    name: file.name,
    size: file.size,
    type: detection.resolved,
    detection,
  };
}
